/**
 * Saya (modelhane) contract — wire shapes AND the vocabularies both sides need
 * as runtime values.
 *
 * Like `auth.contract.ts` this file has NO imports on purpose: it is compiled by
 * all three TypeScript projects — the Worker (`tsconfig.worker.json`), the SPA
 * (`tsconfig.app.json`, via the `src/worker/api/*.contract.ts` glob) and the
 * Node tooling (`tsconfig.node.json`, because `src/db/schema/app.ts` builds the
 * `saya_revision` status CHECK out of `REVISION_STATUSES`). Anything it imported
 * would have to resolve in all three.
 *
 * A `.contract.ts` rather than a `.types.ts` because the value lists are not
 * decoration: the Drizzle column enums, the server-side validators and the
 * SPA's dropdowns all have to agree on the same seven vocabularies, and a
 * second copy of any of them is a silent divergence waiting to happen.
 *
 * How far the database backs that up, exactly: `saya_revision.status` carries a
 * CHECK built from `REVISION_STATUSES` (see `src/db/schema/app.ts`), because a
 * status is what the approval transaction branches on. The rest are text
 * columns with a Drizzle enum in front of them, and the three vocabularies a
 * route actually accepts from a client — handedness, orientation and feature
 * type — are narrowed through the guards below before anything is written.
 * Purchase unit, source and calibration source are server-chosen today (the
 * material seed, the import path and the upload route respectively), so they
 * have no guard: one would be a validator with no input. Claiming a CHECK that
 * is not there would be worse than saying which door is locked.
 *
 * Domain vocabulary stays Turkish (`taslak`, `aynali_cift`, `centik`, `desi`).
 * The repo rule is English identifiers and Turkish only in user-facing UI text;
 * these are neither — they are persisted DATA values, fixed verbatim by the
 * approved module design (its data model writes `status (taslak|onayli|arsiv)`,
 * `handedness (aynali_cift|tek|ayni_yonlu)`, `purchase_unit (desi|mt|tabaka)`),
 * they are the words on the factory floor and in Korgun, and two of them
 * (`desi`, `tabaka`) are trade units with no English name in use here.
 * Translating them would mean a glossary between the database and the people
 * reading it, and a migration to change data nobody asked to change.
 *
 * No money anywhere in this slice: prices and fire allowances are dated tables
 * of their own (see `docs`/the design), computed server-side and filtered by
 * role. Nothing here carries a price, a cost or a currency.
 */

/* ── Vocabularies ───────────────────────────────────────────────────────────── */

/**
 * Revision lifecycle. `taslak` is the only writable state; `onayli` is the one
 * current, immutable revision of a model; `arsiv` is a previously approved one
 * that a newer approval displaced.
 *
 * Also the source of the Drizzle column enum and of the database CHECK in
 * `src/db/schema/app.ts`, so the column and the code cannot disagree.
 */
export const REVISION_STATUSES = ['taslak', 'onayli', 'arsiv'] as const

export type RevisionStatus = (typeof REVISION_STATUSES)[number]

/**
 * How the two instances of a part relate to each other across the pair.
 *
 *   aynali_cift  mirrored pair — the right shoe's piece is the mirror image
 *   tek          a single piece for the pair (one tongue label, say)
 *   ayni_yonlu   two identical, NOT mirrored, pieces
 *
 * The nesting maths needs this because a mirrored instance cannot be produced
 * by rotating the original on a one-way material.
 */
export const HANDEDNESS_VALUES = ['aynali_cift', 'tek', 'ayni_yonlu'] as const

export type Handedness = (typeof HANDEDNESS_VALUES)[number]

/**
 * Which rotations a part may take on its material — the search space of the
 * scale-area assessment, not a preference.
 *
 *   tek_yon    one-way (napped nubuk/süet): same-angle lattices only
 *   cift_yon   0°/180° allowed, which is what makes the 2-piece interlock legal
 *   serbest    any angle
 */
export const ORIENTATION_VALUES = ['tek_yon', 'cift_yon', 'serbest'] as const

export type Orientation = (typeof ORIENTATION_VALUES)[number]

/**
 * What an inner mark on a traced part means. CV proposes, the human decides
 * (design stage 02) — the classification changes whether the mark is cut, is
 * part of the outer path, or is ignored.
 *
 *   delik      a hole to be punched — subtracted from net area
 *   kanal      a channel/score line
 *   centik     a notch on the outer edge, cut with the outline
 *   ic_cizgi   an inner reference line (stitch guide), not cut
 */
export const FEATURE_TYPES = ['delik', 'kanal', 'centik', 'ic_cizgi'] as const

export type FeatureType = (typeof FEATURE_TYPES)[number]

/**
 * The unit a material is actually bought in, which is what consumption has to
 * be expressed in — efficiency is computed on areas, consumption is reported in
 * the purchase unit.
 *
 *   desi     leather, 1 desi = 100 cm² = 10 000 mm²
 *   mt       roll goods (lining), metres off a roll of a real width
 *   tabaka   sheet goods (stiffener), whole sheets of a real size
 */
export const PURCHASE_UNITS = ['desi', 'mt', 'tabaka'] as const

export type PurchaseUnit = (typeof PURCHASE_UNITS)[number]

/**
 * Where a revision's geometry came from — a scanned set, or a graded DXF.
 *
 * Here rather than next to the column for the same reason as the five above:
 * the Drizzle enum, the wire field and the SPA's label table are three sites
 * that must agree, and a third source (a photo, say) has to be one edit.
 */
export const REVISION_SOURCES = ['scan', 'dxf'] as const

export type RevisionSource = (typeof REVISION_SOURCES)[number]

/**
 * How a sheet came to know what a millimetre is on it.
 *
 *   file_dpi   the uploaded file stated its own physical density and that
 *              density was inside the trust window (see `scan-stream.ts`) —
 *              the ordinary case, because the factory's flatbed stamps 300 DPI
 *              into every scan it writes
 *   manual     an admin typed the figures in, on the upload headers or through
 *              `PUT /scans/:id/calibration`. Always wins: it is the recovery
 *              tool, and a human who measured a target is more authoritative
 *              than a preset.
 *
 * `null` — the column and the DTO field are nullable — is the third state and
 * the only one approval refuses: nobody measured this sheet and the file said
 * nothing believable about itself.
 */
export const CALIBRATION_SOURCES = ['manual', 'file_dpi'] as const

export type CalibrationSource = (typeof CALIBRATION_SOURCES)[number]

/**
 * What the AI pre-fill may say a traced piece is cut from.
 *
 * Deliberately NOT the material list: `app.saya_material` is a factory table
 * that changes, and a model asked to choose from it would be guessing at stock
 * codes. Three words is what a photograph can actually support — leather, its
 * lining, and "I cannot tell" — and the screen maps them onto the revision's
 * own materials.
 *
 *   deri       upper leather (the piece the model room calls "saya")
 *   astar      lining
 *   belirsiz   the model would not commit — nothing is pre-filled
 *
 * A vocabulary rather than a free string because both sides check it: the
 * Worker narrows the model's tool call through `isSuggestedMaterial` before it
 * reaches the wire, and the SPA switches on it to pick a material id.
 */
export const SUGGESTED_MATERIALS = ['deri', 'astar', 'belirsiz'] as const

export type SuggestedMaterial = (typeof SUGGESTED_MATERIALS)[number]

/**
 * The model's verdict on OUR tracing, as opposed to on the pieces.
 *
 *   iyi       the outlines match what is on the glass
 *   sorunlu   at least one thing was missed, cut wrong, merged or invented
 *
 * Two words rather than a score, because the screen does two things with it:
 * a green line, or an amber note the operator has to open. A 0–1 number would
 * have to be turned back into exactly this decision, at a threshold nobody
 * could defend.
 */
export const TRACE_REVIEW_VERDICTS = ['iyi', 'sorunlu'] as const

export type TraceReviewVerdict = (typeof TRACE_REVIEW_VERDICTS)[number]

/**
 * What kind of tracing mistake one finding describes.
 *
 *   eksik_parca     a kalıp on the sheet that no outline covers
 *   kotu_kontur     an outline that does not follow the piece's real edge
 *   birlesik_parca  two touching pieces traced as one shape
 *   diger           something real that none of the above names
 *
 * A vocabulary rather than free text because the screen groups and labels by
 * it; the sentence beside it (`TraceFinding.detail`) is where the specifics go.
 * The two hole kinds went with hole tracing itself, and the label kind with
 * the label feature; the parse guard skips unknown kinds, so an old answer
 * still in flight simply loses those findings.
 */
export const TRACE_FINDING_KINDS = [
  'eksik_parca',
  'kotu_kontur',
  'birlesik_parca',
  'diger',
] as const

export type TraceFindingKind = (typeof TRACE_FINDING_KINDS)[number]

/**
 * WHICH model answered a suggest call.
 *
 *   grok    the owner's own xAI account, asked directly (the primary path)
 *   scout   Cloudflare's first-party Llama 4 Scout, through the AI binding
 *
 * On the wire because the two are not interchangeable in the operator's head:
 * the sheet the primary transport could not reach is answered by the fallback in
 * the SAME request, so without this the screen would show a Scout reading of a
 * trace while everybody believes grok read it. The chip is one muted word — it
 * exists so that "the suggestions were worse today" is a question somebody can
 * actually answer.
 *
 * A vocabulary rather than the model ids themselves: `SUGGEST_MODEL` and
 * `XAI_DIRECT_MODEL` are transport detail that changes with a version bump, and
 * a screen is not the place to learn `@cf/meta/llama-4-scout-17b-16e-instruct`.
 */
export const SUGGEST_MODEL_USED_VALUES = ['grok', 'scout'] as const

export type SuggestModelUsed = (typeof SUGGEST_MODEL_USED_VALUES)[number]

/* ── Units ──────────────────────────────────────────────────────────────────── */

/**
 * One desi is 100 cm², i.e. 10 000 mm² — the leather trade's area unit, and the
 * one number the Worker's measurements and the screens' labels must agree on.
 *
 * Here rather than in either of them because it was in both: the Worker's
 * geometry library and the SPA's formatter each carried their own copy of the
 * constant and the division, and the app project cannot import the Worker's
 * libraries — only this file. A conversion factor that exists twice is a
 * conversion factor that can disagree.
 */
export const MM2_PER_DESI = 10_000

/** mm² → desi, the unit leather is bought and costed in. */
export function mm2ToDesi(mm2: number): number {
  return mm2 / MM2_PER_DESI
}

/* ── Master size ────────────────────────────────────────────────────────────── */

/**
 * The shoe sizes the factory actually runs, as one range both sides check.
 *
 * Wide enough for children's and men's work. It lives here rather than in the
 * route and again in the screen because the two drifted once already: a form
 * that refuses a size the API would have accepted looks like a broken keyboard
 * to the operator, and there is no error message to explain it.
 */
export const MIN_MASTER_SIZE = 10
export const MAX_MASTER_SIZE = 70

/** True for a size a revision may be created at. */
export function isMasterSize(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= MIN_MASTER_SIZE &&
    value <= MAX_MASTER_SIZE
  )
}

/* ── Sheets per set ─────────────────────────────────────────────────────────── */

/**
 * How many sheets one revision may hold.
 *
 * A real set runs to a handful of A3 beds; twenty is the ceiling that keeps a
 * runaway uploader from turning one revision into a photo album. It is shared
 * with the SPA because the screen now uploads a whole SELECTION of files in one
 * go: the browser has to know how many slots are left before it starts, or the
 * operator learns the set was full from the ninth request failing.
 */
export const MAX_SCANS_PER_REVISION = 20

/* ── Vocabulary guards ──────────────────────────────────────────────────────── */

/*
 * Narrowing helpers for unvalidated input — a JSON body or a query parameter.
 * They are here rather than next to the route so that the day the SPA has to
 * check the same value before sending it, it checks it with this function
 * rather than a second copy of the list.
 *
 * There is one per vocabulary a route accepts today, and no more: a guard for a
 * value no endpoint takes is a claim about a door that does not exist yet.
 */

/** Narrows a handedness value coming from JSON or a text column. */
export function isHandedness(value: unknown): value is Handedness {
  return typeof value === 'string' && (HANDEDNESS_VALUES as readonly string[]).includes(value)
}

/** Narrows an orientation value coming from JSON or a text column. */
export function isOrientation(value: unknown): value is Orientation {
  return typeof value === 'string' && (ORIENTATION_VALUES as readonly string[]).includes(value)
}

/** Narrows a feature type coming from JSON or a text column. */
export function isFeatureType(value: unknown): value is FeatureType {
  return typeof value === 'string' && (FEATURE_TYPES as readonly string[]).includes(value)
}

/**
 * Narrows a material guess coming back from the model.
 *
 * The one guard here whose input is not a client's JSON but a language model's
 * tool call. It is the same kind of door for the same reason: nothing that has
 * not been checked against the list reaches the wire.
 */
export function isSuggestedMaterial(value: unknown): value is SuggestedMaterial {
  return typeof value === 'string' && (SUGGESTED_MATERIALS as readonly string[]).includes(value)
}

/** Narrows the model's verdict on our tracing. Same door, same reason. */
export function isTraceReviewVerdict(value: unknown): value is TraceReviewVerdict {
  return typeof value === 'string' && (TRACE_REVIEW_VERDICTS as readonly string[]).includes(value)
}

/** Narrows one finding's kind coming back from the model. */
export function isTraceFindingKind(value: unknown): value is TraceFindingKind {
  return typeof value === 'string' && (TRACE_FINDING_KINDS as readonly string[]).includes(value)
}

/* ── Geometry ───────────────────────────────────────────────────────────────── */

/**
 * A closed ring of points in canonical millimetres.
 *
 * The canonical form is fixed and the server enforces it on every write,
 * because every downstream consumer (nesting, DXF export, the pair preview)
 * assumes it:
 *
 *   • Y increases upwards — a scan's pixel space has Y going down, and that
 *     flip happens exactly once, on the way in;
 *   • outer ring counter-clockwise, holes clockwise, so the sign of the shoelace
 *     area alone says which kind of ring it is;
 *   • implicitly closed — the last point is NOT a repeat of the first, and no
 *     point repeats its neighbour;
 *   • 0.01 mm precision, which is finer than any cutter and coarse enough that
 *     the same shape always hashes to the same value.
 *
 * Position is deliberately NOT normalised, and the frame depends on where the
 * part came from: a scan-traced part keeps the scan's own frame (origin at the
 * bottom-left of the SHEET), which is what lets the review screen draw the
 * outline back onto the photograph it came from and what makes "these two parts
 * touched on the glass" a statement the geometry can still support. A part
 * imported from a DXF is translated to its own bounding box instead, because a
 * CAD file's model space is an arbitrary origin nobody scanned. Every consumer
 * of a single part's shape — scale area, strip placement, DXF export — treats
 * translation as free and must not depend on either convention.
 */
export type Ring = [number, number][]

/**
 * One part's cut outline.
 *
 * `holes` is inert cargo: the trace stopped producing hole rings (a hole does
 * not reduce the leather a piece consumes, and desi is what this module
 * measures now), so every new write carries `holes: []`. Legacy rows and DXF
 * imports that DO carry hole rings keep them verbatim — never rendered, never
 * measured, never validated beyond their JSON shape — so nothing is foreclosed
 * if cut-path export ever returns.
 */
export interface PartGeom {
  outer: Ring
  holes: Ring[]
}

/**
 * An inner mark on a part, classified by the operator. `closed` is false for a
 * score line or a stitch guide, true for a punched hole or a closed channel;
 * an open ring is a polyline, not a polygon, and carries no area.
 *
 * NOTHING PRODUCES ONE YET. The auto-trace returns an empty list on purpose and
 * the "Bu iz nedir?" classification tap is a later slice, so every part written
 * so far carries `features: []`. The wire shape and the vocabulary are kept
 * because they are what the write path already accepts and what the geometry
 * hash already covers — changing either later would mean re-hashing approved
 * revisions — but no screen can create one, and nothing reads one back.
 */
export interface PartFeature {
  type: FeatureType
  closed: boolean
  ring: Ring
}

/**
 * The rigid source-to-piece transform (design §5): how the operator normalized
 * a scanned piece — a rotation about the geometry's own centroid plus a
 * translation, in canonical millimetres. `geom_mm` is NEVER rebased; consumers
 * apply this pose on top. `null` means identity (the piece as scanned).
 *
 * Rotation is about the outer ring's centroid so `txMm`/`tyMm` stay meaningful
 * independently of `rotDeg` (see `editor/pose.ts` for the one applier).
 */
export interface PiecePose {
  /** Degrees, counter-clockwise, normalized to (-180, 180]. */
  rotDeg: number
  txMm: number
  tyMm: number
}

/** Narrows a pose coming from JSON. `null` is legitimate and handled by callers. */
export function isPiecePose(value: unknown): value is PiecePose {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false
  const { rotDeg, txMm, tyMm } = value as Record<string, unknown>
  return (
    typeof rotDeg === 'number' &&
    Number.isFinite(rotDeg) &&
    typeof txMm === 'number' &&
    Number.isFinite(txMm) &&
    typeof tyMm === 'number' &&
    Number.isFinite(tyMm)
  )
}

/* ── Requests ───────────────────────────────────────────────────────────────── */

/**
 * One traced part as the browser sends it back after auto-trace and editing.
 *
 * `netMm2` and `perimMm` travel with the geometry but are NOT trusted: the
 * server recomputes both from `geomMm` and stores its own numbers. They are in
 * the payload so a mismatch is visible rather than silent.
 */
export interface TracedPartInput {
  name: string
  /** The contour in the scan's own pixel space, as edited — never destroyed. */
  srcPx: PartGeom
  /** The same contour in canonical millimetres (see `Ring`). */
  geomMm: PartGeom
  features: PartFeature[]
  netMm2: number
  perimMm: number
  /**
   * The piece's operator-entered fields, riding WITH the geometry so the
   * wholesale replace is atomic: the endpoint deletes and re-inserts a sheet's
   * rows, and before this field existed the re-insert reverted every label to
   * its column default until the browser's per-part restores landed — a tab
   * closed in that window silently lost metadata the server had already
   * persisted. Optional so older callers (and the DXF import) stay valid; an
   * absent block means column defaults, exactly as before.
   */
  labels?: TracedPartLabels
}

/** The label fields a wholesale write carries per part — mirrors `PUT /parts/:id`. */
export interface TracedPartLabels {
  materialId: number | null
  qtyPerPair: number
  handedness: Handedness
  orientation: Orientation
  directionVec: [number, number] | null
  bomRef: { proses: string; parcainx: number; skod: string } | null
}

/**
 * One check the trace failed, as the browser reports it back.
 *
 * `code` is machine-readable and English (`touching_suspect`, `too_small`, …);
 * the SPA owns the Turkish sentence. `detail` is English diagnostic text — it
 * is stored, not shown.
 */
export interface ScanQualityFlag {
  code: string
  detail: string
}

/**
 * The verdict of the trace's checks, persisted against the scan.
 *
 * It travels with the parts rather than with the upload because it describes
 * the TRACE, not the file, and the trace runs after the bytes are safe. An
 * empty `flags` array is the meaningful "Uygun" — it is what tells a reloaded
 * draft apart from one that was never checked (`quality: null`). Approval
 * refuses only the `null`: every flag, known or unknown, is an informational
 * note. The first live factory test settled that direction — a stored flag
 * cannot self-clear (re-trace is short-circuited once a verdict is stored), so
 * any flag that gated approval was a permanent block on a set nobody could fix
 * at the scanner. Geometry that is genuinely unusable is refused by the fresh
 * structural checks on save and on approval, which DO self-heal.
 */
export interface ScanQuality {
  flags: ScanQualityFlag[]
}

/** The body of `PUT /api/saya/scans/:id/parts`. */
export interface SaveScanPartsRequest {
  parts: TracedPartInput[]
  /** Omitted only by a caller that did not run the checks — approval refuses it. */
  quality?: ScanQuality
}

/**
 * The body of `PUT /api/saya/scans/:id/calibration` — what a millimetre is on
 * this sheet, measured off a calibration target rather than assumed.
 *
 * Admin only (`canCalibrateScan`), drafts only. The server re-derives every
 * part's canonical millimetres from the pixel contour it stored, so the
 * operator's names, materials, quantities, arrows and sign-offs survive: a
 * calibration that arrives after the labelling must not cost the labelling.
 * Both axes are required together — a flatbed's carriage step and its sensor
 * pitch are calibrated separately, and half a calibration is not one.
 *
 * It stamps `calibrationSource: 'manual'`, overriding whatever the file itself
 * claimed: this is the door a wrong auto-read is corrected through.
 */
export interface CalibrateScanRequest {
  pxPerMmX: number
  pxPerMmY: number
}

/**
 * One traced piece as the AI pre-fill describes it to the model.
 *
 * Everything is in the SCAN'S OWN PIXEL SPACE — the frame the trace produced
 * and the one the browser already has: `interiorPx` is the point the canvas
 * draws this piece's number on, and `bboxPx` is the box it occupies. Sending
 * the millimetre geometry instead would mean asking a model to project
 * coordinates, which is exactly the arithmetic it is worst at.
 *
 * The prompt does NOT quote these pixels. Both pictures are downscaled by the
 * browser on separate ladders, so scan pixels are not the frame of either one;
 * the Worker turns them into percentages of the sheet (using the pixel size on
 * the stored scan row, not one the client could state) before they reach the
 * model, and a percentage is true in every frame at once. What travels on the
 * wire is the raw measurement; what the model reads is the frame-free form.
 *
 * `netDesi` travels as size context: what a piece plausibly is — a quarter, a
 * strap keeper, a collar — is half told by how big it is.
 *
 * The full contour is deliberately NOT sent. The model has the picture; a
 * thousand points per piece would be tokens spent restating what it can see.
 */
export interface SuggestPieceInput {
  /** Position in the rail, 0-based — how the answer addresses this piece. */
  index: number
  /** A point inside the outline (`interiorPoint`), in scan pixels. */
  interiorPx: [number, number]
  /** `[minX, minY, maxX, maxY]` of the outline, in scan pixels. */
  bboxPx: [number, number, number, number]
  /** Outer-ring area, in desi. */
  netDesi: number
}

/**
 * The body of `POST /api/saya/scans/:id/suggest`.
 *
 * Two pictures leave the browser for one question, and BOTH of them are drawn
 * by the browser: `scanJpegBase64` is the sheet as the flatbed wrote it,
 * downscaled, and `overlayPngBase64` is that same sheet with our outlines on
 * it. Sending the second one is the whole reason the model can say anything
 * about the TRACE rather than only about the pieces; a list of coordinates
 * cannot be compared against a photograph by eye, and that comparison is the
 * job.
 *
 * The scan travels with the request rather than being read back out of R2 by
 * the route, and that is a deliberate reversal. The canvas already holds the
 * decoded bitmap — it is what the operator is looking at — so re-fetching the
 * 2–4 MB original server-side bought nothing but an R2 read per suggestion and
 * a prompt an order of magnitude larger than the picture a model actually
 * reads at. `GET /scans/:id/image` still serves the stored original untouched;
 * it is only the suggestion that stopped asking for it.
 *
 * Both fields are bare base64 without the `data:` prefix, and both are
 * downscaled before they leave: the scan to a 1500 px long side as JPEG
 * (photographic content, so a quality knob beats a bigger PNG), the overlay to
 * 1200 px as PNG (flat colour over the sheet, where PNG is smaller and the red
 * outlines stay crisp). The Worker caps each one's decoded size separately and
 * answers 413 above it, and checks each one's magic bytes before building the
 * `data:` URL, exactly the way the upload route checks the file it is handed —
 * the media type a field's name promises is a claim the bytes have to back up.
 */
export interface SuggestScanRequest {
  pieces: SuggestPieceInput[]
  /** The scan itself, downscaled, as base64 JPEG bytes — no `data:` prefix. */
  scanJpegBase64: string
  /** The traced overlay as base64 PNG bytes — no `data:image/png;base64,` prefix. */
  overlayPngBase64: string
}

/**
 * The overlay's ceiling, on the DECODED bytes rather than on the base64 text.
 *
 * Here rather than in the route because both ends need the same number: the
 * browser renders the picture down until it fits under this, and the Worker
 * refuses it above. Two copies of it would mean a client that renders to a
 * ceiling the server does not share, i.e. a 413 nobody can act on.
 *
 * Well under the stored scan's own ceiling on purpose: the overlay exists to be
 * compared against the scan, not to be a second copy of it.
 */
export const MAX_OVERLAY_BYTES = 2 * 1024 * 1024

/**
 * The scan picture's ceiling, decoded — the same contract as the overlay's, for
 * the same reason, on the other half of the payload.
 *
 * The same 2 MiB, and that is not laziness: a 1500 px JPEG at quality 0,85 is a
 * few hundred kilobytes, so both numbers are guard rails against a client that
 * did not downscale rather than working limits, and one number both ends can
 * name is worth more here than two tuned ones. It is its OWN constant all the
 * same, because the two pictures are rendered by different code paths with
 * different step-downs, and a single shared ceiling would tie a change in one
 * to the other.
 *
 * Note what this ceiling is NOT: the stored scan keeps its own upload limit in
 * the route (40 MB), and nothing here shrinks what the factory's flatbed writes
 * into the bucket. This is only the size of the copy the model is shown.
 */
export const MAX_SCAN_IMAGE_BYTES = 2 * 1024 * 1024

/**
 * How many bytes a base64 string decodes to, without decoding it.
 *
 * Four characters carry three bytes, less one for each `=` of padding. Both
 * sides measure the overlay this way — on the payload, not on the string that
 * carries it, a 33 % difference which is the difference between a limit that
 * means something and a number.
 */
export function base64Bytes(base64: string): number {
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0
  return Math.max(0, Math.floor((base64.length * 3) / 4) - padding)
}

/* ── Responses ──────────────────────────────────────────────────────────────── */

/**
 * One BOM line of the model, offered as the material choice for a part.
 *
 * Straight from Korgun's `Model_PD` (read-only mirror): picking the material
 * from the model's own recipe is what makes the later comparison against the
 * BOM exact instead of a name match. A part that genuinely has no BOM line
 * keeps `bomRef: null` ("BOM'da yok").
 */
export interface BomLineOption {
  /** Process code, e.g. `01` (KESİM). */
  proses: string
  /** `Model_PD.Parcainx` — the line's index within the process. */
  parcainx: number
  /** Material stock code (`Model_PD.SKod`). */
  skod: string
  /** Material name — `StokKart.Tanim`, falling back to the line's own `Tanim`. */
  tanim: string
  /** Quantity per pair as Korgun states it, in `birim`. */
  miktar: number
  /** Korgun's unit for `miktar` (`desi`, `mt`, `adet`, …). */
  birim: string
}

export interface SayaPartDto {
  id: number
  scanId: number
  name: string
  /** `app.saya_material.id`, or null while the operator has not chosen one. */
  materialId: number | null
  qtyPerPair: number
  handedness: Handedness
  orientation: Orientation
  /**
   * Unit vector of the grain/nap direction, drawn by the operator — in the
   * PIECE-LOCAL frame: apply the pose's rotation for display, never persist
   * it rotated (a normalization change must not rotate physical grain twice).
   */
  directionVec: [number, number] | null
  /** The Korgun BOM line this part cuts from, or null for "BOM'da yok". */
  bomRef: { proses: string; parcainx: number; skod: string } | null
  /** Outer-ring area, server-computed from `geomMm`. Holes are ignored. */
  netMm2: number
  /** Outer-ring perimeter, server-computed. Holes are ignored. */
  perimMm: number
  /** The per-part visual sign-off approval requires. */
  visualOk: boolean
  /** Rigid normalization pose, or null for identity — see {@link PiecePose}. */
  pose: PiecePose | null
  geomMm: PartGeom
  features: PartFeature[]
}

/**
 * One scanned sheet of a revision.
 *
 * A revision holds one OR MORE of them: a size-38 saya set does not fit on one
 * A3 bed under the "pieces must not touch, must not touch borders" rule the
 * quality checks enforce, so digitizing a real set means several sheets, and the
 * design's own review mock is captioned "tarama 1/2". Uploading appends a sheet;
 * re-shooting one replaces THAT sheet (`POST /api/saya/scans/:id/replace`) and
 * nothing else — and 409s rather than adding one if that sheet has meanwhile
 * gone. Each sheet carries its own calibration and its own trace verdict, and
 * approval requires every one of them to be calibrated and clean.
 *
 * `checksumSha256` is what makes the append idempotent: re-sending a file this
 * revision already stores answers 200 with the sheet that is already there
 * instead of 201 with a duplicate, so a response lost on the way back to the
 * tablet costs a retry and not a phantom sheet.
 *
 * Parts point at the sheet they were traced from (`SayaPartDto.scanId`), which
 * is what lets one sheet be re-shot without touching the labelling done on the
 * others.
 */
export interface SayaScanDto {
  id: number
  revisionId: number
  /**
   * False for a DXF import, which has parts but no picture. The SPA uses it to
   * decide whether `GET /api/saya/scans/:id/image` is worth asking for.
   */
  hasImage: boolean
  pxWidth: number
  pxHeight: number
  /** Calibration, null while the scan is uncalibrated (drafts may be). */
  pxPerMmX: number | null
  pxPerMmY: number | null
  /**
   * Where that calibration came from, null exactly when there is none.
   *
   * The screen needs the distinction: `file_dpi` is the scanner's own stamp and
   * needs no attention, `manual` is somebody's measurement, and null is the one
   * state a set cannot be approved in — and, since the upload reads the file's
   * density itself, no longer the ordinary state of a real scan.
   */
  calibrationSource: CalibrationSource | null
  checksumSha256: string | null
  /** Blocking-check results from the trace, null before it has run. */
  quality: ScanQuality | null
}

export interface SayaMaterialDto {
  id: number
  name: string
  /** True when the material has a grain/nap, which constrains orientation. */
  directional: boolean
  purchaseUnit: PurchaseUnit
  /** Real roll width for `mt` goods, null otherwise. */
  rollWidthMm: number | null
  /** Real sheet size `[width, height]` for `tabaka` goods, null otherwise. */
  sheetMm: [number, number] | null
  /** Placement gap between pieces on this material. */
  gapMm: number
}

export interface SayaRevisionSummary {
  id: number
  /** Korgun `Model_M.ModelKod` — a validated value, deliberately not an FK. */
  modelKod: string
  revNo: number
  /** Ana beden: the size every part in this revision was cut at. */
  masterSize: number
  status: RevisionStatus
  source: RevisionSource
  /** ISO timestamp. */
  createdAt: string
  createdBy: string
  approvedAt: string | null
  approvedBy: string | null
  partCount: number
  /**
   * Net area per pair, in desi, grouped by material — the geometry figure the
   * model card shows next to the BOM. `materialId: null` collects the parts that
   * have no material chosen yet, so the total is never quietly short.
   */
  netDesiPerPairByMaterial: {
    materialId: number | null
    materialName: string | null
    netDesi: number
  }[]
}

export interface SayaRevisionDetail extends SayaRevisionSummary {
  /** The explicit "Tüm saya kalıpları yüklendi" tick approval requires. */
  setConfirmed: boolean
  /** Every sheet of the set, oldest first — see {@link SayaScanDto}. */
  scans: SayaScanDto[]
  parts: SayaPartDto[]
}

/**
 * What `POST /api/saya/revisions/:id/import-dxf` answers with: the revision as
 * it now stands, plus everything the reader could not take at face value.
 *
 * The warnings are the parser's own English diagnostics, and they are on the
 * wire because the alternative is silence about geometry that was ALTERED on
 * the way in — a polyline arc read as a chord, a chain the file never closed,
 * a block that held two outlines. The import still succeeds: what the reader
 * cannot represent at all (a part drawn with ARC/CIRCLE/ELLIPSE/SPLINE records)
 * is refused outright instead, because a part that imports with a piece of its
 * outline missing looks complete and is not.
 */
export interface DxfImportResult extends SayaRevisionDetail {
  warnings: string[]
}

/**
 * What the model says about one traced piece. A HINT, never a decision.
 *
 * Nothing here is written to the database by the suggest route: it answers, the
 * screen pre-fills the controls the operator has not touched, and the operator's
 * own save is what persists. That is the whole safety argument for letting a
 * language model near a set that ends up at a cutter.
 *
 * `qtyPerPair` is what the modelhane wrote on the sheet — the chips read
 * "Saya 2×1" (upper leather, 2 per pair) or "AST 2×1" (astar) — and null when
 * there is no such notation near the piece.
 *
 * `confidence` is the model's own 0–1 figure. The screen uses it as a floor, so
 * a shrug fills nothing in rather than filling something in badly.
 */
export interface PieceSuggestion {
  /** The `SuggestPieceInput.index` this answers. */
  index: number
  material: SuggestedMaterial
  /** Quantity read off a chip near the piece, or null when none was found. */
  qtyPerPair: number | null
  /** The model's own confidence, 0–1. */
  confidence: number
}

/**
 * One thing the model says our tracer got wrong. A REPORT, never an edit.
 *
 * Nothing in the app acts on a finding: there is no repair tool on this screen
 * by design (a trace that is wrong is a scan that was wrong, and the honest fix
 * is at the scanner), so a finding's whole job is to send the operator back to
 * the picture with something specific to look for.
 *
 * `detail` is Turkish, because it is read verbatim by the person at the
 * scanner — the one string in this module that a model writes and a human
 * reads. It is asked for in Turkish, length-clamped server-side, and rendered
 * as text and never as markup.
 *
 * `pieceIndex` addresses the same rail position as `SuggestPieceInput.index`,
 * and is null for a finding that is about no traced piece at all — which is
 * exactly the most valuable kind, a kalıp on the sheet that nothing traced.
 */
export interface TraceFinding {
  kind: TraceFindingKind
  /** One Turkish sentence naming what is wrong. */
  detail: string
  /** The rail position this is about, or null when it is about the sheet. */
  pieceIndex: number | null
}

/**
 * The model's read of our own drawing: a verdict and the reasons for it.
 *
 * `findings` may be empty on an `iyi` sheet and is the point of a `sorunlu`
 * one. It is capped and every entry re-validated server-side like everything
 * else that comes back from a model.
 */
export interface TraceReview {
  overall: TraceReviewVerdict
  findings: TraceFinding[]
}

/**
 * What `POST /api/saya/scans/:id/suggest` answers with.
 *
 * A piece the model declined to describe is simply absent — the list is not
 * padded to the request's length, and the screen matches by `index`.
 *
 * `traceReview` is null when the answer carried nothing usable about the
 * tracing: the pieces it did report are still worth having, and a review that
 * defaulted to "iyi" would be the machine approving our drawing on the strength
 * of having said nothing about it.
 *
 * `modelUsed` names WHICH of the two transports produced all of it. It is not
 * telemetry: the fallback runs inside the same request, so this is the only
 * thing that distinguishes a grok reading from the Scout reading that silently
 * took its place when xAI was unreachable.
 */
export interface SuggestScanResponse {
  pieces: PieceSuggestion[]
  traceReview: TraceReview | null
  modelUsed: SuggestModelUsed
}

/** What a mutation with nothing to hand back answers — `DELETE /revisions/:id`. */
export interface SayaOkResponse {
  ok: true
}

/**
 * Everything the Saya tab of a model card needs, in one request.
 *
 * `current` and `draft` are the two revisions the screen actually works with —
 * the approved one it shows and the editable one it writes to; `revisions` is
 * the read-only history strip.
 */
export interface SayaModelPayload {
  modelKod: string
  /** Newest first. */
  revisions: SayaRevisionSummary[]
  /** The single `onayli` revision, or null when the model has none yet. */
  current: SayaRevisionDetail | null
  /** The newest `taslak` revision, or null when nothing is in progress. */
  draft: SayaRevisionDetail | null
  bomLines: BomLineOption[]
  materials: SayaMaterialDto[]
}
